import { Alert, Box, Grid, Stack, Typography } from '@mui/material';
import { Button } from '../../../components';
import { StyleGuidePage } from '../style-guide-page';

export const StyleGuideGridsOverview = () => {
  return (
    <StyleGuidePage title="Grid Overview">
      <Grid container mb={2}>
        <Grid item xs={12} lg={8}>
          <Box mb={2}>
            <Alert severity="info">
              NOTE: This is a living style guide and is subject to grow and change. The following is
              partial documentation for the initial phase of the Kitchen Sink and will later be
              broken out into different sections with more details and lists of props.
            </Alert>
          </Box>
          <Typography mb={1}>
            A typical grid is usually comprised of at least 2 components: Table and a MobileTable.
            The following is a list of possible components that make up a grid as well as example
            links to specific instances for each.
          </Typography>
          <Typography mb={1}>
            This component will be the most used to create new pages in the application. However,
            there are other page type components created for different areas of the application that
            you should be aware of when developing:
            <ul>
              <li>
                <strong>Table</strong>: For rendering a table of data with columns.
                <Typography mt={0.5} mb={1.5}>
                  <Button
                    variant="contained"
                    color="primary"
                    to="/vendors/contracts/in-progress"
                    asLink
                    id="table-example"
                  >
                    Example
                  </Button>
                </Typography>
              </li>
              <li>
                <strong>MobileTable</strong>: For rendering a mobile version of the table comprised
                of accordion cards. Thus far MobileTable has been done manually for each instance of
                a list/grid, however, there is now a specific component that will dynamically
                generate a mobile view based on available columns. This component is injected into
                the <em>ResponsiveComponent</em> prop of the Table component
                <Typography mt={0.5} mb={1.5}>
                  <Button
                    variant="contained"
                    color="primary"
                    to="/admin/user-tools/framers"
                    asLink
                    id="mobile-table-example"
                  >
                    Example
                  </Button>
                </Typography>
              </li>
              <li>
                <strong>Tabs</strong>: For changing the results, columns, and even filters belonging
                to a grid instance.
                <Typography mt={0.5} mb={1.5}>
                  <Button
                    variant="contained"
                    color="primary"
                    to="/admin/user-tools/users"
                    asLink
                    id="tabs-example"
                  >
                    Example
                  </Button>
                </Typography>
              </li>
              <li>
                <strong>FiltersLayout</strong>: For rendering/wrapping the filters in a
                conditionally collapsible container
                <Typography mt={0.5} mb={1.5}>
                  <Button
                    variant="contained"
                    color="primary"
                    to="/vendors/contracts/in-progress"
                    asLink
                    id="filter-layout-example"
                  >
                    Example
                  </Button>
                </Typography>
              </li>
              <li>
                <strong>FiltersButtons</strong>: For rendering Apply Filters and Reset buttons with
                built-in conditional display logic and responsive design
                <Typography mt={0.5} mb={1.5}>
                  <Button
                    variant="contained"
                    color="primary"
                    to="/vendors/contracts/in-progress"
                    asLink
                    id="filter-button-example"
                  >
                    Example
                  </Button>
                </Typography>
              </li>
              <li>
                <strong>Pagination</strong>: For rendering pagination for a table component,
                typically directly follows a Table component and should be wrapped in conditional
                logic so that it only shows when there are results
                <Typography mt={0.5} mb={1.5}>
                  <Button
                    variant="contained"
                    color="primary"
                    to="/vendors/contracts/in-progress"
                    asLink
                    id="pagination-example"
                  >
                    Example
                  </Button>
                </Typography>
              </li>
              <li>
                <strong>Bulk Download Table</strong>: For rendering a table of downloadable files
                with the option of selecting multiples and downloading a .zip file. To view the
                example, after following the link, open the Sample Contract Documents accordion,
                fill out the form, and click the generate button.
                <Typography mt={0.5} mb={1.5}>
                  <Stack direction="row" gap={0.5}>
                    <Button
                      variant="contained"
                      color="primary"
                      to="/sales/sales-tools/marketing"
                      asLink
                      id="bulk-download-example"
                    >
                      Example
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      to="/kitchen-sink/components/grids/bulk-download-grid"
                      asLink
                      id="bulk-download-docs"
                    >
                      Docs
                    </Button>
                  </Stack>
                </Typography>
              </li>
            </ul>
          </Typography>
        </Grid>
      </Grid>
    </StyleGuidePage>
  );
};
